import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Factory, Tractor, Truck, Building, Briefcase, SunDim, Zap,
  TrendingUp, Activity, Shield, Cpu, BarChart3, Leaf, ArrowRight, Check,
  Clock, ShieldCheck, Lightbulb, FileText, AlertTriangle, ChevronDown
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative pt-32 pb-20 overflow-hidden" data-testid="b2b-hero">
    <div className="blob-pink absolute -top-20 -right-32 opacity-60" />
    <div className="blob-pink absolute top-1/3 -left-32 opacity-40" />
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7 relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-soft border border-brand/20 mb-7">
          <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-deep">Zakelijke energieoplossingen</span>
        </div>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-navy-900 leading-[1.02] text-balance" data-testid="b2b-hero-title">
          De toekomst van bedrijfsenergie: <span className="gradient-pink-text">opslag, zon en handel.</span>
        </h1>
        <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-xl">
          Voor industrie, vastgoed, agrarische sector, logistiek en MKB. Wij ontwerpen, financieren en realiseren grootschalige batterijopslag, zakelijke zonneparken en EMS-platformen, met meetbare ROI en concrete oplossingen voor netcongestie.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4">
          <Link to="/contact" state={{ calculator: { source: "b2b_strategy_call" } }} className="btn-pink" data-testid="b2b-hero-cta">
            Plan strategiegesprek <ArrowRight size={18} />
          </Link>
          <a href="#b2b-calculator" className="btn-ghost">Bereken uw rendement</a>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-5 max-w-lg">
          <Stat tag="EIA-aftrek" value="40%" sub="tot van investering" />
          <Stat tag="MIA/Vamil" value="45 / 75%" sub="aftrek + afschrijving" />
          <Stat tag="Terugverdien" value="3-6 jr" sub="grootschalig" />
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-5 relative z-10">
        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
          <img src="https://customer-assets.emergentagent.com/job_solar-power-next/artifacts/kx22o0dz_Energie-8-5-1.webp" alt="Helios Bright zonnepaneleninstallatie op bedrijfspand met wagenpark" loading="eager" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-brand font-bold mb-1">Live · Industrieel</div>
            <div className="font-heading font-bold text-navy-900">2,4 MWh systeem · Peak shaving actief</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Stat = ({ tag, value, sub }) => (
  <div className="border-l-2 border-brand pl-3">
    <div className="text-[10px] uppercase tracking-widest text-brand-deep font-bold">{tag}</div>
    <div className="font-heading text-2xl font-black text-navy-900 leading-tight">{value}</div>
    <div className="text-[11px] text-slate-500">{sub}</div>
  </div>
);

/* ---------------- INDUSTRIES ---------------- */
const Industries = () => {
  const items = [
    { icon: Factory, title: "Industrie", desc: "Productie-omgevingen met hoge piekvermogens en continue processen." },
    { icon: Building, title: "Bedrijfshallen", desc: "Grote dakoppervlakken, ideaal voor zon + opslagcombinaties." },
    { icon: Tractor, title: "Agrarische sector", desc: "Stallen, kassen en akkers met seizoensgebonden energieprofielen." },
    { icon: Truck, title: "Logistiek & DC's", desc: "EV-laadinfra, koeling en netcongestie-knelpunten." },
    { icon: Building2, title: "Vastgoed", desc: "Beleggings- en bedrijfspanden met ESG-doelstellingen." },
    { icon: Briefcase, title: "MKB", desc: "Vaste kantoren en bedrijfsruimtes met groeiende energievraag." },
    { icon: SunDim, title: "Zonneparken", desc: "Grondgebonden installaties tot enkele MWp's." },
    { icon: Zap, title: "Netcongestie-cases", desc: "Bedrijven met aansluitknelpunten of wachtlijst voor verzwaring." },
  ];
  return (
    <section className="section-pad bg-slate-50/70" data-testid="b2b-industries">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Sectoren</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Wij leveren aan elke schaal en sector.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="group bg-white border border-slate-200 rounded-3xl p-6 hover:border-brand/40 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center mb-4 group-hover:bg-brand group-hover:text-white transition-colors">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-navy-900 mb-1">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- SOLUTIONS ---------------- */
const Solutions = () => {
  const items = [
    { icon: Activity, title: "Peak shaving", desc: "Vlak uw piekvermogen af en verlaag de capaciteitstarief-component van uw netbeheerderfactuur direct." },
    { icon: AlertTriangle, title: "Netcongestie oplossen", desc: "Voor bedrijven op wachtlijst voor verzwaring, opslag levert vermogen wanneer het net dat niet kan." },
    { icon: TrendingUp, title: "Energiehandel & arbitrage", desc: "Verdien op dynamische APX-prijzen. Laad op bij lage tarieven, ontlaad op pieken." },
    { icon: Cpu, title: "EMS & Smart Grid", desc: "AI-gestuurd energiebeheer dat productie, verbruik, prijzen en onbalans real-time optimaliseert." },
    { icon: Shield, title: "Backup & continuïteit", desc: "Black-start mogelijk: uw kritieke processen blijven draaien bij stroomstoringen." },
    { icon: BarChart3, title: "Onbalansmarkt (FCR/aFRR)", desc: "Lever flexibiliteit aan TenneT en genereer extra inkomsten via balanceringsdiensten." },
    { icon: SunDim, title: "Zakelijke zonneparken", desc: "Daken, carports, grondgebonden installaties, van 100 kWp tot meerdere MWp's." },
    { icon: Leaf, title: "ESG & CO₂-reductie", desc: "Concrete bijdrage aan Scope 1, 2 en deels 3 emissiereductie. Rapportage-klaar voor CSRD." },
  ];
  return (
    <section className="section-pad" data-testid="b2b-solutions">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-12 items-start mb-14">
          <div className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Onze oplossingen</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Eén partner voor de volledige energie-transitie van uw onderneming.</h2>
          </div>
          <p className="text-slate-600 text-lg">Van ontwerp tot oplevering tot exploitatie. Wij combineren engineering, financiering en handelsexpertise tot één rendabele propositie.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white rounded-3xl border border-slate-200 p-6 hover:border-brand/40 hover:shadow-[0_20px_50px_rgba(255,47,125,0.08)] transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl gradient-pink text-white flex items-center justify-center mb-4">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-lg text-navy-900 mb-2">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- B2B CALCULATOR ---------------- */
const BusinessCalculator = () => {
  const [peakKw, setPeakKw] = useState([150]);
  const [annualKwh, setAnnualKwh] = useState([250000]);
  const [batteryKwh, setBatteryKwh] = useState([400]);
  const [solarKwp, setSolarKwp] = useState([150]);
  const [feedInKwh, setFeedInKwh] = useState([30000]);
  const [sector, setSector] = useState("industrie");

  const result = useMemo(() => {
    const pricePerKwh = 0.18; // zakelijk gemiddelde
    const capacityTariff = 50; // €/kW/jaar voor piekcapaciteit (netbeheer)
    const solarProductionPerKwp = 950; // kWh per kWp in NL zakelijk
    const arbitrageMargin = 0.07; // €/kWh per cyclus

    // Peak shaving savings: ~25% piekreductie haalbaar met batterij
    const peakReduction = Math.min(peakKw[0] * 0.25, batteryKwh[0] * 0.3);
    const peakShavingSavings = peakReduction * capacityTariff;

    // Solar production & self consumption
    const solarProduction = solarKwp[0] * solarProductionPerKwp;
    const selfUsed = Math.min(solarProduction, annualKwh[0]) * 0.75;
    const surplus = Math.max(0, solarProduction - selfUsed);
    const solarSavings = selfUsed * pricePerKwh + surplus * pricePerKwh * 0.4;

    // Arbitrage with battery: 1 cycle per day = 365 cycles
    const arbitrageRevenue = batteryKwh[0] * 0.85 * 365 * arbitrageMargin;

    // Investment estimate
    const batteryInvest = batteryKwh[0] * 480; // €/kWh
    const solarInvest = solarKwp[0] * 700; // €/kWp
    const totalInvest = batteryInvest + solarInvest;

    // Fiscale aftrek estimate (EIA 40% × tarief 25.8% VPB = ~10.3% netto, plus MIA optioneel)
    const eiaNet = totalInvest * 0.40 * 0.258;
    const miaNet = totalInvest * 0.36 * 0.258; // gemiddeld
    const fiscalBenefit = eiaNet + miaNet * 0.5; // schatting met MIA gedeeltelijk

    const totalAnnualBenefit = peakShavingSavings + solarSavings + arbitrageRevenue;
    const netInvest = Math.max(1, totalInvest - fiscalBenefit);
    const payback = netInvest / totalAnnualBenefit;
    const co2 = (selfUsed + surplus + batteryKwh[0] * 200) * 0.4;

    return {
      peakShavingSavings: Math.round(peakShavingSavings),
      solarSavings: Math.round(solarSavings),
      arbitrageRevenue: Math.round(arbitrageRevenue),
      totalAnnualBenefit: Math.round(totalAnnualBenefit),
      totalInvest: Math.round(totalInvest),
      fiscalBenefit: Math.round(fiscalBenefit),
      netInvest: Math.round(netInvest),
      payback: payback.toFixed(1),
      co2: Math.round(co2),
    };
  }, [peakKw, annualKwh, batteryKwh, solarKwp]);

  return (
    <section id="b2b-calculator" className="section-pad relative bg-slate-50/70" data-testid="b2b-calculator">
      <div className="absolute inset-0 noise-bg opacity-50" />
      <div className="blob-pink absolute top-10 right-0 opacity-40" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">ROI-calculator</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">ROI-calculator voor energieopslag</h2>
          <p className="mt-4 text-sm font-semibold uppercase tracking-widest text-slate-500">Voor bedrijven met 10 kWp tot 500 kWp zonnepanelen</p>
          <p className="mt-5 text-slate-600 text-lg">Indicatieve berekening op basis van gemiddelde Nederlandse tarieven. Onze adviseurs leveren een gedetailleerd business case rapport.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-7">
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-[2rem] p-7 lg:p-10">
            <CalcRow label="Sector">
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger className="w-full rounded-xl h-12 border-slate-200" data-testid="b2b-calc-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="industrie">Industrie</SelectItem>
                  <SelectItem value="logistiek">Logistiek / DC</SelectItem>
                  <SelectItem value="agrarisch">Agrarisch</SelectItem>
                  <SelectItem value="vastgoed">Vastgoed</SelectItem>
                  <SelectItem value="mkb">MKB / Kantoor</SelectItem>
                </SelectContent>
              </Select>
            </CalcRow>
            <CalcSlider label="Piekvermogen (kW)" value={peakKw} onChange={setPeakKw} min={50} max={2000} step={10} display={`${peakKw[0]} kW`} tid="b2b-calc-peak" />
            <CalcSlider label="Jaarlijks verbruik (kWh)" value={annualKwh} onChange={setAnnualKwh} min={50000} max={1000000} step={10000} display={`${(annualKwh[0] / 1000).toLocaleString("nl-NL")} MWh`} tid="b2b-calc-usage" />
            <CalcSlider label="Jaarlijkse teruglevering (kWh)" value={feedInKwh} onChange={setFeedInKwh} min={0} max={500000} step={5000} display={`${feedInKwh[0].toLocaleString("nl-NL")} kWh`} tid="b2b-calc-feedin" />
            <CalcSlider label="Batterijcapaciteit (kWh)" value={batteryKwh} onChange={setBatteryKwh} min={100} max={5000} step={50} display={`${batteryKwh[0]} kWh`} tid="b2b-calc-battery" />
            <CalcSlider label="Zonnepanelen vermogen (kWp)" value={solarKwp} onChange={setSolarKwp} min={10} max={500} step={5} display={`${solarKwp[0]} kWp`} tid="b2b-calc-solar" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-navy-900 text-white rounded-[2rem] p-7 lg:p-9 relative overflow-hidden"
            data-testid="b2b-calc-results"
          >
            <div className="blob-pink absolute -top-20 -right-12 opacity-50" />
            <div className="relative z-10">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-2">Business case</p>

              {/* Key metrics, prominent at the top */}
              <div className="grid grid-cols-3 gap-3 mt-5 mb-6 pb-6 border-b border-white/10" data-testid="b2b-calc-key-metrics">
                <KeyStat
                  label="Besparing/jr"
                  value={`€ ${(result.totalAnnualBenefit / 1000).toFixed(0)}k`}
                  testid="key-savings"
                />
                <KeyStat
                  label="Terugverdien"
                  value={`${result.payback} jr`}
                  testid="key-payback"
                />
                <KeyStat
                  label="CO₂/jr"
                  value={`${(result.co2 / 1000).toFixed(0)}t`}
                  testid="key-co2"
                />
              </div>

              <div className="space-y-4 mt-5">
                <RowKv k="Peak shaving besparing" v={`€ ${result.peakShavingSavings.toLocaleString("nl-NL")} / jr`} />
                <RowKv k="Zonne-energie besparing" v={`€ ${result.solarSavings.toLocaleString("nl-NL")} / jr`} />
                <RowKv k="Arbitrage / handel" v={`€ ${result.arbitrageRevenue.toLocaleString("nl-NL")} / jr`} />
                <RowKv k="Totaal jaarlijks voordeel" v={`€ ${result.totalAnnualBenefit.toLocaleString("nl-NL")}`} big />
                <div className="h-px bg-white/10 my-2" />
                <RowKv k="Investering (indicatie)" v={`€ ${result.totalInvest.toLocaleString("nl-NL")}`} />
                <RowKv k="Fiscaal voordeel (EIA/MIA)" v={`− € ${result.fiscalBenefit.toLocaleString("nl-NL")}`} accent />
                <RowKv k="Netto investering" v={`€ ${result.netInvest.toLocaleString("nl-NL")}`} />
                <RowKv k="Terugverdientijd" v={`${result.payback} jaar`} big />
                <RowKv k="CO₂-reductie" v={`${result.co2.toLocaleString("nl-NL")} kg / jr`} />
              </div>
              <Link
                to="/contact"
                state={{ calculator: { source: "b2b_calculator", peakKw: peakKw[0], annualKwh: annualKwh[0], batteryKwh: batteryKwh[0], solarKwp: solarKwp[0], feedInKwh: feedInKwh[0], estimated_savings: result.totalAnnualBenefit, payback_years: parseFloat(result.payback) } }}
                className="mt-7 w-full btn-pink !text-white"
                data-testid="b2b-calc-cta"
              >
                Vraag een gratis business case aan <ArrowRight size={18} />
              </Link>
              <p className="mt-4 text-xs text-slate-400 text-center">Indicatief, exacte berekening op basis van uw verbruiksprofiel.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const CalcRow = ({ label, children }) => (
  <div className="mt-7 first:mt-0">
    <label className="text-sm font-semibold text-navy-900 mb-3 block">{label}</label>
    {children}
  </div>
);
const CalcSlider = ({ label, value, onChange, min, max, step, display, tid }) => (
  <div className="mt-7" data-testid={`${tid}-row`}>
    <div className="flex items-center justify-between mb-3">
      <label className="text-sm font-semibold text-navy-900">{label}</label>
      <span className="text-sm font-bold text-brand tabular-nums">{display}</span>
    </div>
    <Slider value={value} onValueChange={onChange} min={min} max={max} step={step} data-testid={tid} />
  </div>
);
const RowKv = ({ k, v, big, accent }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-sm text-slate-300">{k}</span>
    <span className={`font-heading font-black tabular-nums ${big ? "text-2xl" : "text-base"} ${accent ? "text-brand" : ""}`}>{v}</span>
  </div>
);

const KeyStat = ({ label, value, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className="font-heading font-black text-2xl xl:text-[1.65rem] leading-tight gradient-pink-text tabular-nums whitespace-nowrap">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1.5">{label}</div>
  </div>
);

/* ---------------- SUBSIDIES TABLE ---------------- */
const SubsidiesTable = () => {
  const rows = [
    { name: "EIA, Energie-investeringsaftrek", percent: "40% extra aftrek", who: "BV / NV / eenmanszaak", apply: "Aanmelden binnen 3 maanden na opdracht via RVO", combine: "Combineerbaar met KIA, NIET met MIA op zelfde investering" },
    { name: "MIA, Milieu-investeringsaftrek", percent: "tot 45% aftrek", who: "Bedrijven met milieuvriendelijke activa op Milieulijst", apply: "Aanvragen via RVO binnen 3 maanden", combine: "Combineerbaar met Vamil, niet met EIA op zelfde investering" },
    { name: "Vamil, Willekeurige Afschrijving", percent: "75% direct afschrijven", who: "Bedrijven met Milieulijst-investeringen", apply: "Direct toepasbaar in aangifte VPB / IB", combine: "Combineert standaard met MIA, cashflow voordeel in jaar 1" },
    { name: "KIA, Kleinschaligheidsinvesteringsaftrek", percent: "tot 28% (schaalend)", who: "MKB met investeringen € 2.401 – € 387.580", apply: "Automatisch in IB/VPB aangifte", combine: "Combineerbaar met EIA én MIA" },
    { name: "Flex-E subsidie", percent: "tot 40% projectkosten", who: "Bedrijven met flexibiliteits- / netcongestie projecten", apply: "RVO openstelling 2026, call-based", combine: "Combineerbaar met EIA in beperkte gevallen" },
    { name: "SPRILA / SPULA", percent: "investeringssubsidie", who: "Logistiek & laadinfrastructuur", apply: "Via RVO, gepland 2026", combine: "Beperkt combineerbaar met EIA" },
    { name: "Subsidieregeling Netcongestie (SDE-flex)", percent: "exploitatiesubsidie", who: "Bedrijven die flexvermogen leveren bij congestie", apply: "Per netbeheerder-gebied, openstellingen 2026", combine: "Combineerbaar met fiscale regelingen" },
    { name: "Provinciale & gemeentelijke subsidies", percent: "varieert per regio", who: "Lokaal afhankelijk (NH, ZH, NB, Gelderland top-3)", apply: "Per provincie / gemeente loket", combine: "Stapelbaar met EIA/MIA/Vamil" },
    { name: "BTW-teruggave", percent: "21%", who: "BTW-plichtige ondernemingen", apply: "Standaard via aangifte omzetbelasting", combine: "Altijd van toepassing op zakelijke investeringen" },
  ];
  const [open, setOpen] = useState(false);
  return (
    <section className="section-pad" data-testid="b2b-subsidies">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Subsidies & fiscale voordelen 2026</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">De volledige financiële stapel voor uw investering.</h2>
          <p className="mt-5 text-slate-600 text-lg">Helios Bright regelt EIA-, MIA-, Vamil- en Flex-E aanvragen volledig voor u. Slim stapelen kan tot <strong className="text-brand-deep">25-35% directe aftrek</strong> opleveren op uw bruto-investering.</p>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="b2b-subsidies-table-content"
          className="group inline-flex items-center gap-3 px-5 py-3 rounded-full border border-slate-200 bg-white hover:border-brand/40 hover:shadow-[0_8px_24px_rgba(255,47,125,0.1)] transition-all duration-300 mb-6"
          data-testid="b2b-subsidies-toggle"
        >
          <span className="font-heading font-bold text-navy-900 text-sm">
            {open ? "Verberg subsidieregelingen" : "Bekijk alle subsidieregelingen"}
          </span>
          <span className={`w-7 h-7 rounded-full bg-brand-soft text-brand flex items-center justify-center transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
            <ChevronDown size={16} strokeWidth={2.5} />
          </span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              id="b2b-subsidies-table-content"
              key="subsidies-table"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.25 } }}
              className="overflow-hidden"
              data-testid="b2b-subsidies-table-wrapper"
            >
              <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-[0_20px_60px_rgba(15,23,42,0.04)] mb-2">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-navy-900 text-white">
                      <tr>
                        <th className="text-left p-5 font-heading font-bold">Regeling</th>
                        <th className="text-left p-5 font-heading font-bold">Voordeel</th>
                        <th className="text-left p-5 font-heading font-bold">Wie komt in aanmerking</th>
                        <th className="text-left p-5 font-heading font-bold">Aanvragen</th>
                        <th className="text-left p-5 font-heading font-bold hidden xl:table-cell">Stapelen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.name} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                          <td className="p-5 font-semibold text-navy-900 align-top">{r.name}</td>
                          <td className="p-5 align-top"><span className="inline-block px-2.5 py-1 rounded-full bg-brand-soft text-brand-deep font-bold text-xs whitespace-nowrap">{r.percent}</span></td>
                          <td className="p-5 text-slate-600 align-top">{r.who}</td>
                          <td className="p-5 text-slate-600 align-top">{r.apply}</td>
                          <td className="p-5 text-slate-600 align-top hidden xl:table-cell">{r.combine}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          <FiscalCard title="EIA, concreet" lines={["40% extra aftrek bovenop normale afschrijving", "Effectief netto voordeel ~10,3% bij VPB 25,8%", "Op RVO Energielijst code 260202 (opslag) en 250101 (zon)"]} />
          <FiscalCard title="MIA + Vamil, combinatie" lines={["Tot 45% aftrek + 75% willekeurig afschrijven", "Netto cashflow voordeel jaar 1: tot ~30%", "Gekoppeld aan Milieulijst-codes (LCA-eisen batterij)"]} />
          <FiscalCard title="Flex-E + netcongestie" lines={["Exploitatiesubsidie voor flex- en congestiediensten", "Per gebied verschillende openstellingen", "Helios regelt aansluiting bij netbeheerder & TenneT"]} />
        </div>

        <div className="mt-10 bg-gradient-to-br from-brand-soft/40 to-white border border-brand/20 rounded-3xl p-7 lg:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="b2b-subsidie-cta">
          <div>
            <h3 className="font-heading text-2xl font-black text-navy-900">Subsidiecheck binnen 48 uur.</h3>
            <p className="text-slate-600 mt-1">Wij brengen uw exacte aftrek- en subsidiepositie in kaart, kosteloos en vrijblijvend.</p>
          </div>
          <Link to="/contact" state={{ calculator: { source: "subsidiecheck" } }} className="btn-pink" data-testid="b2b-subsidiecheck-cta">
            Subsidiecheck aanvragen <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const FiscalCard = ({ title, lines }) => (
  <div className="bg-white border border-slate-200 rounded-3xl p-7">
    <h4 className="font-heading font-black text-lg text-navy-900 mb-4">{title}</h4>
    <ul className="space-y-2">
      {lines.map((l) => (
        <li key={l} className="flex items-start gap-2 text-sm text-slate-700">
          <Check size={16} className="text-brand mt-0.5 flex-shrink-0" strokeWidth={3} />
          <span>{l}</span>
        </li>
      ))}
    </ul>
  </div>
);

/* ---------------- COMPARISON TABLE ---------------- */
const ComparisonTable = () => {
  const rows = [
    ["Jaarlijkse energiekosten", "€ 180.000", "€ 96.000", "−47%"],
    ["Capaciteitstarief netbeheer", "€ 22.500", "€ 14.000", "−38%"],
    ["Opbrengst arbitrage / flex", "€ 0", "€ 38.000", "+ nieuw"],
    ["Backup beschikbaarheid", "Nee", "Ja (black-start)", "—"],
    ["Netcongestie-risico", "Hoog", "Beheerd", "—"],
    ["CO₂-uitstoot per jaar", "240 t", "82 t", "−66%"],
    ["Beheerbaarheid pieken", "Reactief", "AI / EMS gestuurd", "—"],
    ["ESG / CSRD scoring", "Standaard", "Aantoonbaar lager", "—"],
  ];
  return (
    <section className="section-pad bg-navy-900 text-white relative overflow-hidden" data-testid="b2b-comparison">
      <div className="blob-pink absolute -top-32 -right-20 opacity-40" />
      <div className="blob-pink absolute -bottom-20 -left-20 opacity-20" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Vergelijking</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-balance">Zonder batterij vs. mét Helios Bright systeem.</h2>
          <p className="mt-5 text-slate-300 text-lg">Voorbeeldcase: industriële locatie, 800 MWh/jaar verbruik, 250 kW piek, 600 kWp zon + 1.000 kWh batterij.</p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/10">
                <tr>
                  <th className="text-left p-5 font-heading font-bold">Metric</th>
                  <th className="text-left p-5 font-heading font-bold">Huidige situatie</th>
                  <th className="text-left p-5 font-heading font-bold">Met Helios Bright</th>
                  <th className="text-left p-5 font-heading font-bold">Verschil</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-white/10">
                    <td className="p-5 font-semibold">{r[0]}</td>
                    <td className="p-5 text-slate-300">{r[1]}</td>
                    <td className="p-5 text-white font-bold">{r[2]}</td>
                    <td className="p-5"><span className="text-brand font-bold">{r[3]}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-brand/10 border-t border-brand/30">
                <tr>
                  <td className="p-5 font-heading font-black text-lg">Netto voordeel jaar 1</td>
                  <td className="p-5"></td>
                  <td className="p-5"></td>
                  <td className="p-5"><span className="font-heading font-black text-2xl text-brand">€ 130.500</span></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- WHY NOW ---------------- */
const WhyNow = () => {
  const items = [
    { icon: TrendingUp, title: "Stijgende netkosten", desc: "Capaciteits- en transporttarieven stijgen jaarlijks 8-15%. Eigen flexibiliteit betaalt zich snel terug." },
    { icon: AlertTriangle, title: "Netcongestie op rood", desc: "Grote delen van NL kennen wachtlijsten van 3-7 jaar voor verzwaring. Batterij = directe oplossing." },
    { icon: Clock, title: "Afbouw saldering", desc: "Vanaf 2027 wordt eigen verbruik exponentieel waardevoller dan teruglevering." },
    { icon: Zap, title: "Volatiele energieprijzen", desc: "Dynamische tarieven creëren een marktconforme verdienkans voor opslag." },
    { icon: Leaf, title: "Verduurzaming verplicht", desc: "CSRD / CBAM / EED rapportageplicht vraagt om aantoonbare CO₂-reductie." },
    { icon: ShieldCheck, title: "ESG vereisten investeerders", desc: "Banken en investeerders eisen meetbare ESG-prestaties bij financiering." },
  ];
  return (
    <section className="section-pad" data-testid="b2b-why-now">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Waarom nú investeren</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Zes redenen om niet langer te wachten.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-3xl p-7 hover:border-brand/40 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center mb-4">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-lg text-navy-900 mb-2">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQs ---------------- */
const FaqGroup = ({ eyebrow, title, faqs, testid }) => (
  <div data-testid={testid}>
    <div className="mb-6">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-2">{eyebrow}</p>
      <h3 className="font-heading text-2xl md:text-3xl font-black text-navy-900">{title}</h3>
    </div>
    <Accordion type="single" collapsible className="space-y-3">
      {faqs.map((f, i) => (
        <AccordionItem
          key={i}
          value={`${testid}-${i}`}
          className="bg-white border border-slate-200 rounded-2xl px-5 data-[state=open]:border-brand/40 data-[state=open]:shadow-[0_10px_30px_rgba(255,47,125,0.08)] transition-all"
        >
          <AccordionTrigger className="font-heading font-bold text-base text-navy-900 hover:no-underline py-4 text-left">{f.q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed pb-4 text-sm">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const B2BFaqs = () => (
  <section className="section-pad bg-slate-50/70" data-testid="b2b-faqs">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">FAQ</p>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Antwoorden voor besluitvormers.</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-10">
        <FaqGroup
          testid="faq-subsidies"
          eyebrow="Subsidies"
          title="Subsidies & fiscaal"
          faqs={[
            { q: "Welk fiscaal voordeel is concreet haalbaar?", a: "Voor een industriële batterij (RVO-code 260202) realiseren onze klanten typisch 25-35% netto investeringsvoordeel via EIA + Vamil + BTW. Bij MIA-lijst-batterijen kan dit oplopen naar 35-45%." },
            { q: "Kan ik EIA en MIA combineren op één investering?", a: "Nee, op exact dezelfde investering niet. Wel kunt u op zon EIA aanvragen én op de batterij MIA + Vamil, dat is de meest voorkomende stack en gestapeld kan dit hogere voordelen opleveren." },
            { q: "Hoe snel moet ik aanvragen?", a: "EIA en MIA-aanvragen dienen binnen 3 maanden na opdrachtbevestiging via RVO te zijn ingediend. Helios Bright doet dit voor u, inclusief de juiste codering." },
            { q: "Wat is de Flex-E subsidie?", a: "Flex-E is een nieuwe regeling gericht op flexibele vermogensoplossingen die netcongestie verlichten. Tot ~40% van projectkosten subsidiabel, afhankelijk van openstelling en gebied." },
          ]}
        />
        <FaqGroup
          testid="faq-netcongestie"
          eyebrow="Netcongestie"
          title="Netcongestie"
          faqs={[
            { q: "Lost een batterij netcongestie écht op?", a: "Ja, voor lokale capaciteitsknelpunten. De batterij absorbeert pieken en levert tijdens netcontracts-vermogensbeperking. Veel klanten met wachtlijst voor verzwaring kunnen hiermee dóór terwijl ze wachten." },
            { q: "Krijg ik een gereduceerd contract aangeboden?", a: "TenneT en regionale netbeheerders bieden capaciteitsbeperkingsovereenkomsten (CBC) en non-firm transportovereenkomsten, vaak met korting op transporttarief. Wij faciliteren deze gesprekken." },
            { q: "Wat is de gemiddelde wachttijd voor verzwaring?", a: "Afhankelijk van regio 1 tot 7 jaar. In Brabant, Gelderland en delen van NH zijn de wachttijden het langst. Batterij is in de meeste gevallen sneller realiseerbaar." },
          ]}
        />
        <FaqGroup
          testid="faq-batterij"
          eyebrow="Batterij & technologie"
          title="Batterij & techniek"
          faqs={[
            { q: "Welke batterijtechnologie wordt toegepast?", a: "Voornamelijk LFP (Lithium-IJzer-Fosfaat) chemie vanwege veiligheid, levensduur (6.000-10.000 cycli) en cobaltvrije productie. Brandwerend opgesteld in containerunits of indoor cabinets." },
            { q: "Wat is de levensduur van een industrieel systeem?", a: "Premium LFP-systemen behouden minimaal 70% capaciteit na 15 jaar bij dagelijks gebruik (1 cyclus/dag). Met 10 jaar fabrieksgarantie op cellen en omvormers." },
            { q: "Welke schaal levert Helios Bright?", a: "Van 100 kWh (groot MKB) tot multi-MWh systemen voor industriële complexen en zonneparken. Inclusief EMS, monitoring en handelsplatform-koppeling." },
          ]}
        />
        <FaqGroup
          testid="faq-roi"
          eyebrow="ROI"
          title="ROI & business case"
          faqs={[
            { q: "Wat is de gemiddelde terugverdientijd?", a: "Voor industriële projecten met combinatie peak shaving + arbitrage + flex: 3 tot 6 jaar netto (na EIA/MIA/Vamil). Pure backup-toepassingen hebben langere terugverdientijd maar leveren operationele zekerheid." },
            { q: "Welke verdienmodellen zijn beschikbaar?", a: "Peak shaving (capaciteitstarief reductie), energie-arbitrage op APX, onbalansmarkt (FCR/aFRR), congestie-flexdiensten en optimalisatie eigen verbruik bij zon. Wij koppelen aan handelsplatform of partner-aggregator." },
            { q: "Is leasing of PPA mogelijk?", a: "Ja. Wij werken samen met gespecialiseerde financiers voor Energy-as-a-Service constructies, lease en PPA, vaak met directe positieve maandcashflow." },
          ]}
        />
      </div>
    </div>
  </section>
);

/* ---------------- CLOSING CTA ---------------- */
const Closing = () => (
  <section className="py-24 relative overflow-hidden" data-testid="b2b-closing">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 p-10 lg:p-16">
        <div className="blob-pink absolute -top-32 -left-32 opacity-60" />
        <div className="blob-pink absolute -bottom-32 -right-32 opacity-40" />
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-4">Voor ondernemers & vastgoedinvesteerders</p>
            <h3 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white text-balance">De energietransitie is geen kostenpost, het is uw volgende winstcentrum.</h3>
            <p className="text-slate-300 mt-6 text-lg max-w-xl">Elke maand wachten kost capaciteitstarief, mist arbitrage-marge en stelt subsidies in gevaar. Plan vandaag een strategiegesprek met een Helios Bright senior consultant.</p>
          </div>
          <div className="flex flex-col gap-4">
            <Link to="/contact" state={{ calculator: { source: "b2b_strategy_call" } }} className="btn-pink !text-white text-center" data-testid="b2b-closing-cta-1">
              Plan strategiegesprek <ArrowRight size={18} />
            </Link>
            <Link to="/contact" state={{ calculator: { source: "energy_scan" } }} className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 text-center" data-testid="b2b-closing-cta-2">
              Gratis energiescan
            </Link>
            <Link to="/contact" state={{ calculator: { source: "subsidiecheck" } }} className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 text-center" data-testid="b2b-closing-cta-3">
              Subsidiecheck aanvragen
            </Link>
          </div>
        </div>

        <div className="relative mt-12 pt-10 border-t border-white/10 grid md:grid-cols-3 gap-6 text-white">
          <ClosingPoint icon={Lightbulb} title="48u reactietijd" desc="Senior consultant binnen 2 werkdagen." />
          <ClosingPoint icon={FileText} title="Volledige aanvraag" desc="EIA, MIA, Vamil, Flex-E, door ons geregeld." />
          <ClosingPoint icon={ShieldCheck} title="ISO-gecertificeerd" desc="EPV-installateur, NEN-3140, ISO 9001." />
        </div>
      </div>
    </div>
  </section>
);

const ClosingPoint = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-xl bg-brand/20 text-brand flex items-center justify-center flex-shrink-0">
      <Icon size={18} />
    </div>
    <div>
      <div className="font-heading font-bold">{title}</div>
      <div className="text-sm text-slate-300">{desc}</div>
    </div>
  </div>
);

/* ---------------- PAGE ---------------- */
const Zakelijk = () => (
  <main data-testid="b2b-page">
    <Hero />
    <BusinessCalculator />
    <Industries />
    <Solutions />
    <SubsidiesTable />
    <ComparisonTable />
    <WhyNow />
    <B2BFaqs />
    <Closing />
  </main>
);

export default Zakelijk;
